import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppLayout } from "../components/layout/AppLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { ScoreRing } from "../components/ui/ScoreRing";
import { useAuth } from "../contexts/AuthContext";
import { api, ApiError } from "../lib/api";
import type { InterviewMessage } from "../types";

const STAGE_LABEL: Record<string, string> = {
  introduction: "Introduction",
  collaboration: "Collaboration",
  problem_solving: "Problem Solving",
  leadership: "Leadership",
  wrap_up: "Wrap Up",
};
const STAGES = ["introduction", "collaboration", "problem_solving", "leadership", "wrap_up"];

const TIPS = [
  "Be specific with your examples (Use STAR method)",
  "Show your collaboration style and communication",
  "Be yourself — we value authenticity more than perfect answers",
];

export default function InterviewChat() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { refreshSubscription } = useAuth();

  const [messages, setMessages] = useState<InterviewMessage[]>([]);
  const [stage, setStage] = useState("introduction");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set only when a hard rate limit (daily free cap / monthly Pro soft cap) is hit —
  // NOT a generic "out of credits" flag. The interview's 1 credit is already spent at
  // /start, so it never gates sending messages within a session that's already begun.
  const [rateLimited, setRateLimited] = useState<{ code: string; message: string } | null>(null);
  const [title, setTitle] = useState("AI Chat Interview");
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!id) return;
    api
      .get<{ interview: { stage: string; title: string }; messages: InterviewMessage[] }>(`/api/interview/${id}`)
      .then((res) => {
        setMessages(res.messages);
        setStage(res.interview.stage);
        setTitle(res.interview.title || "AI Chat Interview");
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load interview information."));
  }, [id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function send() {
    if (!id || !input.trim() || rateLimited) return;
    const content = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content, stage, created_at: new Date().toISOString() }]);
    setSending(true);
    setError(null);
    try {
      const res = await api.post<{ stage: string; message: string; isComplete: boolean }>(`/api/interview/${id}/message`, {
        content,
      });
      setMessages((prev) => [...prev, { role: "ai", content: res.message, stage: res.stage, created_at: new Date().toISOString() }]);
      setStage(res.stage);
      if (res.isComplete) {
        setCompleting(true);
        await api.post(`/api/interview/${id}/complete`);
        await refreshSubscription();
        navigate(`/app/interview/${id}/report`);
      }
    } catch (err) {
      if (err instanceof ApiError && (err.code === "DAILY_LIMIT_EXCEEDED" || err.code === "SOFT_CAP_REACHED")) {
        setRateLimited({ code: err.code, message: err.message });
      } else {
        setError(err instanceof Error ? err.message : "Failed to send message.");
      }
    } finally {
      setSending(false);
      setCompleting(false);
    }
  }

  async function endInterview() {
    if (!id) return;
    setEnding(true);
    setError(null);
    try {
      await api.post(`/api/interview/${id}/complete`);
      await refreshSubscription();
      navigate(`/app/interview/${id}/report`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to end interview.");
      setEnding(false);
    }
  }

  const stageIndex = STAGES.indexOf(stage);
  const progressPct = Math.round(((stageIndex + (messages.length > 0 ? 0.5 : 0)) / STAGES.length) * 100);
  const userAnswerCount = messages.filter((m) => m.role === "user").length;

  return (
    <AppLayout>
      <div className="mb-1 text-xs text-slate-400">
        <button onClick={() => navigate("/app")} className="hover:text-navy">← Back to Dashboard</button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-navy sm:text-xl">AI Chat Interview</h1>
            <span className="rounded-full bg-teal/10 px-2 py-0.5 text-[10px] font-semibold text-teal-dark">In Progress</span>
          </div>
          <p className="mt-1 max-w-lg text-xs text-slate-500">
            Answer the questions naturally. Our AI interviewer asks follow-up questions and evaluates your responses in real time.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {rateLimited && (
            <button
              onClick={() => navigate("/pricing")}
              className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-teal to-teal-dark px-3.5 py-2 text-xs font-bold text-white shadow-sm transition hover:brightness-110"
            >
              ⚡ Upgrade Plan
            </button>
          )}
          <Card className="border border-slate-100 !p-3">
            <p className="text-[11px] font-semibold text-navy">{title}</p>
            <p className="mt-0.5 text-[10px] text-slate-400">{userAnswerCount} answers so far</p>
          </Card>
        </div>
      </div>

      {rateLimited && (
        <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3.5 text-rose-900 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-500 text-white font-bold text-sm">⚡</span>
              <div>
                <p className="text-xs font-bold sm:text-sm">
                  {rateLimited.code === "DAILY_LIMIT_EXCEEDED" ? "Daily Free Limit Reached" : "Monthly Pro Limit Reached"}
                </p>
                <p className="text-[11px] text-rose-700">{rateLimited.message}</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/pricing")}
              className="inline-flex items-center gap-1 rounded-lg bg-navy px-3.5 py-2 text-xs font-bold text-white transition hover:bg-navy-light"
            >
              ⚡ Upgrade Plan / Buy Credits →
            </button>
          </div>
        </div>
      )}

      {error && <Card className="mt-3 border border-coral/30 bg-coral/5 text-xs text-coral">{error}</Card>}

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[220px_1fr_260px]">
        {/* Left: stage list + tips */}
        <div className="space-y-4 lg:order-1">
          <Card className="border border-slate-100 !p-3">
            <div className="space-y-1">
              {STAGES.map((s, i) => {
                const status = i < stageIndex ? "done" : i === stageIndex ? "current" : "pending";
                return (
                  <div
                    key={s}
                    className={`flex items-center gap-2.5 rounded-lg px-2 py-2 ${status === "current" ? "bg-teal/10" : ""}`}
                  >
                    <span
                      className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                        status === "done"
                          ? "bg-teal text-white"
                          : status === "current"
                          ? "bg-navy text-white"
                          : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {status === "done" ? "✓" : i + 1}
                    </span>
                    <div>
                      <p className={`text-xs font-semibold ${status === "pending" ? "text-slate-400" : "text-navy"}`}>
                        {STAGE_LABEL[s]}
                      </p>
                      <p className="text-[10px] text-slate-400 capitalize">
                        {status === "done" ? "Completed" : status === "current" ? "In Progress" : "Pending"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="border border-slate-100 !p-3">
            <p className="text-xs font-semibold text-navy">💡 Interview Tips</p>
            <ul className="mt-2 space-y-1.5">
              {TIPS.map((t, i) => (
                <li key={i} className="flex items-start gap-1.5 text-[10px] leading-relaxed text-slate-500">
                  <span className="mt-0.5 text-teal">✓</span>
                  {t}
                </li>
              ))}
            </ul>
          </Card>
        </div>

        {/* Center: chat */}
        <Card className="flex h-[70vh] min-h-[480px] flex-col border border-slate-100 !p-0 lg:order-2">
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex items-start gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                {m.role === "ai" && (
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-navy text-xs">🤖</span>
                )}
                <div
                  className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "rounded-br-sm bg-navy text-white" : "rounded-tl-sm bg-slate-50 text-navy"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {(sending || completing) && (
              <div className="flex items-start gap-2">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-navy text-xs">🤖</span>
                <div className="rounded-2xl rounded-tl-sm bg-slate-50 px-3.5 py-2.5 text-sm text-slate-400">
                  {completing ? "Generating analysis report..." : "AI is typing..."}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="flex items-center gap-2 border-t border-slate-100 px-3 py-2.5">
            <input
              className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm focus:border-teal focus:outline-none disabled:bg-slate-50 disabled:text-slate-400"
              placeholder={rateLimited ? "Rate limit reached. Upgrade to keep chatting..." : "Type your answer here..."}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !sending && !rateLimited && send()}
              disabled={sending || completing || Boolean(rateLimited)}
            />
            <Button size="sm" onClick={send} disabled={!input.trim() || Boolean(rateLimited)} loading={sending}>
              Send
            </Button>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-[10px] text-slate-400">
            <span>🎙 Audio On · 📷 Camera On</span>
            <button
              onClick={endInterview}
              disabled={ending || messages.filter((m) => m.role === "user").length === 0}
              className="rounded-full border border-slate-200 px-3 py-1.5 font-semibold text-slate-500 transition hover:border-coral hover:text-coral disabled:opacity-40"
            >
              {ending ? "Ending..." : "End Interview"}
            </button>
          </div>
        </Card>

        {/* Right: progress */}
        <div className="space-y-4 lg:order-3">
          <Card className="border border-slate-100 text-center">
            <p className="text-xs font-semibold text-navy">Interview Progress</p>
            <div className="mt-3 flex justify-center">
              <ScoreRing value={progressPct} size={100} strokeWidth={9} />
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              {stageIndex + 1} / {STAGES.length} stages
            </p>
          </Card>

          <Card className="border border-slate-100">
            <p className="text-xs font-semibold text-navy">Live Analysis</p>
            <div className="mt-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-3 text-center">
              <p className="text-[11px] leading-relaxed text-slate-400">
                Your detailed communication, collaboration, problem-solving and leadership scores will be ready right after you finish this interview.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
